import { useMemo, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  contestantLabel,
  formatDecision,
  isDisagreement,
  verdictFor,
  type LabelValue,
  type ModelLabCaseSummary,
  type ModelLabResultSummary,
  type Task,
} from '@/lib/model-lab'
import { DecisionChip } from './badges'

type Filter = 'all' | 'disagreements' | 'unlabeled' | 'errors'

interface Props {
  readonly task: Task
  readonly contestantKeys: readonly string[]
  readonly cases: readonly ModelLabCaseSummary[]
  readonly results: readonly ModelLabResultSummary[]
  readonly labels: ReadonlyMap<string, LabelValue>
  readonly onOpenCase: (caseId: string) => void
}

function labelText(task: Task, label: LabelValue | undefined): string {
  if (!label) return '—'
  if (task === 'sms') return `${Object.keys(label.ratings ?? {}).length} rated`
  return formatDecision(label.decision)
}

export function CasesTable({ task, contestantKeys, cases, results, labels, onOpenCase }: Props) {
  const [filter, setFilter] = useState<Filter>(task === 'sms' ? 'all' : 'disagreements')

  const rows = useMemo(() => {
    const byCase = new Map<string, ModelLabResultSummary[]>()
    for (const r of results) byCase.set(r.case_id, [...(byCase.get(r.case_id) ?? []), r])

    return cases
      .filter((c) => c.task === task)
      .map((c) => ({ labCase: c, caseResults: byCase.get(c.id) ?? [], label: labels.get(c.case_key) }))
      .filter(({ caseResults, label }) => {
        switch (filter) {
          case 'disagreements': return isDisagreement(task, caseResults)
          case 'unlabeled': return !label
          case 'errors': return caseResults.some((r) => r.status !== 'ok')
          default: return true
        }
      })
  }, [cases, results, labels, task, filter])

  return (
    <div className="space-y-3">
      <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          {task !== 'sms' && <TabsTrigger value="disagreements">Models disagree</TabsTrigger>}
          <TabsTrigger value="unlabeled">Unlabelled</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Decided</TableHead>
              <TableHead>Production</TableHead>
              <TableHead>Your label</TableHead>
              {contestantKeys.map((key) => (
                <TableHead key={key} className="font-mono text-xs">
                  {contestantLabel(key)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3 + contestantKeys.length} className="py-8 text-center text-sm text-muted-foreground">
                  No conversations match this filter.
                </TableCell>
              </TableRow>
            )}
            {rows.map(({ labCase, caseResults, label }) => (
              <TableRow key={labCase.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onOpenCase(labCase.id)}>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                  {labCase.source_created_at ? new Date(labCase.source_created_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell className="text-xs">
                  {task === 'sms' ? 'sent' : formatDecision(labCase.production_output?.decision)}
                </TableCell>
                <TableCell className="text-xs font-medium">{labelText(task, label)}</TableCell>
                {contestantKeys.map((key) => {
                  const result = caseResults.find((r) => r.contestant_key === key)
                  if (!result) {
                    return (
                      <TableCell key={key} className="text-xs text-muted-foreground">
                        pending
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell key={key}>
                      <DecisionChip
                        decision={task === 'sms' ? 'text' : result.decision}
                        verdict={verdictFor(task, result, label)}
                        errorText={result.status === 'ok' ? null : result.status.replace('_', ' ')}
                      />
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
